'use client'

import { FC, useCallback } from 'react'
import React from 'react'
import Image from 'next/image'
import { cn, formatPriceToNumber, PriceRange, useMediaQuery, useProductPrice } from '@segway/core'
import { GetCategoryProductsQuery } from '@segway/core/src/graphql/generated/graphql'

import { Link } from '@/i18n/navigation'
import reportGTMEvent from '@/utils/gtm'

import { SavePrice } from '../Homepage/_components'

type TProducts = NonNullable<GetCategoryProductsQuery['products']>
type TProductItems = NonNullable<TProducts['items']>
type TProductItem = NonNullable<TProductItems[number]>

interface ProductItemProps extends React.HTMLAttributes<HTMLDivElement> {
  product: TProductItem
  className?: string
  urlKeyFullLink?: string
  index?: number
}

const ProductItem: FC<ProductItemProps> = ({ product, className, urlKeyFullLink, index }) => {
  const { name, image, price_range, url_key, sw_simple_description } = product
  const { isDiscount, finalPrice, regularPrice } = useProductPrice(price_range)
  const responsive = useMediaQuery()

  const roundUpAndFormat = useCallback((num: number) => {
    return num.toFixed(2).toString() ?? ''
  }, [])

  /**
   * GTM: select_item
   */
  const handleReportProductGTMEvent = useCallback(() => {
    if (urlKeyFullLink) {
      const item_category = product.gtm_item_categories
        ? JSON.parse(product.gtm_item_categories)
        : {
            item_category: '',
            item_category2: '',
            item_category3: '',
            item_category4: '',
            item_category5: '',
          }

      reportGTMEvent({
        event: 'select_item',
        ecommerce: {
          item_list_name: urlKeyFullLink,
          items: [
            {
              item_id: product.sku,
              item_name: product.name,
              msrp: formatPriceToNumber(product.price_range.minimum_price.regular_price.value!)
                .numberValue,
              price: formatPriceToNumber(product.price_range.minimum_price.final_price.value!)
                .numberValue,
              item_brand: 'Segway',
              index,
              ...item_category,
            },
          ],
        },
      })
    }
  }, [product, urlKeyFullLink, index])

  return (
    <div
      className={cn(
        `product-container relative rounded-[20px] xl:border-solid xl:pb-[24px]`,
        className,
      )}>
      <div className="m-auto flex items-center justify-center overflow-hidden rounded-[12px] bg-gray-1 xl:rounded-xl xl:bg-[#F3F3F4]">
        {isDiscount && (
          <SavePrice
            originPrice={roundUpAndFormat(regularPrice.value ?? 0)}
            finalPrice={roundUpAndFormat(finalPrice.value ?? 0)}
          />
        )}
        <Link
          className="relative flex aspect-square h-[165px] w-[165px] items-center xl:h-[288px] xl:w-[288px] 2xl:h-[348px] 2xl:w-[348px]"
          href={`/${url_key}` || ''}
          onClick={handleReportProductGTMEvent}>
          <Image
            src={image?.url || ''}
            alt={name || ''}
            fill
            style={{ objectFit: 'contain' }}
            className="duration-300 hover:lg:scale-110"
          />
        </Link>
      </div>
      <div className="mt-3 w-full overflow-hidden text-ellipsis whitespace-nowrap px-1 font-miSansMedium380 text-[10px] text-[#6E6E73] xl:mt-4 xl:h-[17px] xl:text-base">
        {sw_simple_description}
      </div>
      <div className="mt-[6px] line-clamp-2 overflow-hidden text-ellipsis px-1 font-miSansMedium380 text-base text-black xl:mt-2 xl:line-clamp-1 xl:text-2xl">
        <Link href={`/${url_key}` || ''} onClick={handleReportProductGTMEvent}>
          {name}
        </Link>
      </div>
      <div className="mt-3 px-1">
        <PriceRange
          priceProps={{
            containerStyle: 'text-base',
            currencyStyle: `${responsive && responsive.xl ? 'text-[14px]' : 'text-[12px]'} mr-[2px]`,
            textStyle: 'text-[14px] xl:text-[16px]',
          }}
          originalPriceProps={{
            containerStyle: 'text-base',
            currencyStyle: `${responsive && responsive.xl ? 'text-[14px]' : 'text-[12px]'} mr-[2px]`,
            textStyle: 'text-[14px] xl:text-[16px]',
          }}
          priceRange={price_range}
        />
      </div>
    </div>
  )
}

export default ProductItem
